import type { CommentComposerDraft } from "./comment-composer-model";
import type { AnnotationComment, PreviewAnnotation } from "./types";

export interface AnnotationCommentThreadNode {
    comment: AnnotationComment;
    replies: AnnotationCommentThreadNode[];
}

export function getAnnotationComments(annotation: PreviewAnnotation): AnnotationComment[] {
    if (annotation.thread?.comments?.length) return annotation.thread.comments;
    if (!annotation.thread?.text) return [];
    return [
        {
            id: legacyCommentId(annotation.id),
            text: annotation.thread.text,
            document: annotation.thread.document,
            references: annotation.thread.references,
            createdAt: annotation.createdAt,
            updatedAt: annotation.updatedAt,
        },
    ];
}

export function buildAnnotationCommentTree(
    comments: AnnotationComment[],
): AnnotationCommentThreadNode[] {
    const nodes = new Map<string, AnnotationCommentThreadNode>();
    for (const comment of comments) {
        nodes.set(comment.id, {
            comment,
            replies: [],
        });
    }

    const roots: AnnotationCommentThreadNode[] = [];
    for (const comment of comments) {
        const node = nodes.get(comment.id);
        if (!node) continue;
        const parent = comment.parentId && comment.parentId !== comment.id
            ? nodes.get(comment.parentId)
            : null;
        if (parent) {
            parent.replies.push(node);
        } else {
            roots.push(node);
        }
    }
    return roots;
}

export function hasCommentDraftContent(draft: CommentComposerDraft): boolean {
    return draft.text.trim().length > 0 || draft.references.length > 0;
}

export function updateAnnotationComment(
    annotation: PreviewAnnotation,
    commentId: string,
    draft: CommentComposerDraft,
    timestamp: string,
): PreviewAnnotation {
    const comments = getAnnotationComments(annotation).map((comment) =>
        comment.id === commentId
            ? {
                ...comment,
                text: draft.text,
                document: draft.document,
                references: draft.references,
                updatedAt: timestamp,
            }
            : comment,
    );
    return {
        ...annotation,
        updatedAt: timestamp,
        thread: {
            comments,
        },
    };
}

export function deleteAnnotationComment(
    annotation: PreviewAnnotation,
    commentId: string,
    timestamp: string,
): PreviewAnnotation {
    const comments = getAnnotationComments(annotation);
    const deleteIds = collectCommentDescendants(comments, commentId);
    const nextComments = comments.filter((comment) => !deleteIds.has(comment.id));
    return {
        ...annotation,
        updatedAt: timestamp,
        thread: {
            comments: nextComments,
        },
    };
}

function collectCommentDescendants(
    comments: AnnotationComment[],
    commentId: string,
): Set<string> {
    const ids = new Set<string>([commentId]);
    let changed = true;
    while (changed) {
        changed = false;
        for (const comment of comments) {
            if (comment.parentId && ids.has(comment.parentId) && !ids.has(comment.id)) {
                ids.add(comment.id);
                changed = true;
            }
        }
    }
    return ids;
}

function legacyCommentId(annotationId: string): string {
    return `${annotationId}:legacy`;
}
